/**
 * components/shared/Brokers/brokerSelectionUtils.ts
 * Utility functions for broker selection form validation and processing
 */

import { getPropFirmById, getAccountTypesForPropFirm } from '@/data/propFirmCatalog';
import type { AccountType } from '@/types/shared/AccountType';
import { isPersonalLikeAccountType } from '@/utils/accountType';

/**
 * Account data structure for broker selection validation
 */
export interface BrokerSelectionAccountData {
  accountType?: AccountType;
  selectedAssetClasses: string[];
  propFirmId?: string;
  accountStage?: string;
  selectedBrokerId?: string;
  customAccountTypeName?: string;
}

/**
 * Validation result structure
 */
export interface BrokerSelectionValidation {
  isValid: boolean;
  errorMessage?: string;
}

/**
 * Validate account setup prerequisites (without broker selection)
 */
export const validateAccountSetupPrerequisites = (accountData: BrokerSelectionAccountData): BrokerSelectionValidation => {
  const { accountType, selectedAssetClasses, propFirmId, customAccountTypeName } = accountData;
  
  if (!accountType) {
    return {
      isValid: false,
      errorMessage: 'Please select an account type first'
    };
  }
  
  // Check custom account type name if "other" is selected
  if (accountType === 'other' && (!customAccountTypeName || customAccountTypeName.trim().length === 0)) {
    return {
      isValid: false,
      errorMessage: 'Please specify your custom account type'
    };
  }
  
  // Personal-like accounts (personal, retirement, business, trust, pooled, other) and paper accounts need asset classes
  if ((isPersonalLikeAccountType(accountType) || accountType === 'paper') && selectedAssetClasses.length === 0) {
    return {
      isValid: false,
      errorMessage: 'Please select asset classes first'
    };
  }
  
  // Prop accounts need prop firm details
  if (accountType === 'prop' && (!propFirmId || selectedAssetClasses.length === 0)) {
    return {
      isValid: false,
      errorMessage: 'Please select a prop firm and asset classes first'
    };
  }
  
  return { isValid: true };
};

/**
 * Validate broker selection form data
 */
export const validateBrokerSelection = (accountData: BrokerSelectionAccountData): BrokerSelectionValidation => {
  const { selectedBrokerId } = accountData;
  
  // First check prerequisites
  const prerequisiteValidation = validateAccountSetupPrerequisites(accountData);
  if (!prerequisiteValidation.isValid) {
    return prerequisiteValidation;
  }
  
  // Then check broker selection
  if (!selectedBrokerId) {
    return {
      isValid: false,
      errorMessage: 'Please select a broker to continue'
    };
  }
  
  return { isValid: true };
};

/**
 * Get display name for the account context
 */
export const getAccountDisplayName = (accountData: BrokerSelectionAccountData): string => {
  const { accountType, propFirmId, accountStage, customAccountTypeName } = accountData;
  
  if (!accountType) {
    return 'Unknown Account';
  }
  
  // Handle prop firm accounts
  if (accountType === 'prop' && propFirmId) {
    const propFirm = getPropFirmById(propFirmId);
    const accountTypes = getAccountTypesForPropFirm(propFirmId);
    const accountTypeInfo = accountTypes.find(type => type.id === accountStage);
    
    if (propFirm && accountTypeInfo) {
      return `${propFirm.name} - ${accountTypeInfo.name}`;
    } else if (propFirm) {
      return `${propFirm.name}`;
    }
    
    return 'Prop Firm Account';
  }
  
  // Handle custom account type
  if (accountType === 'other' && customAccountTypeName) {
    return customAccountTypeName;
  }
  
  // Handle standard account types
  const displayNames: Record<AccountType, string> = {
    personal: 'Personal Account',
    paper: 'Paper Trading Account',
    prop: 'Prop Firm Account',
    retirement: 'Retirement Account',
    business: 'Business Account',
    trust: 'Trust Account',
    pooled: 'Pooled Investment Fund',
    other: 'Custom Account'
  };
  
  return displayNames[accountType] || 'Unknown Account';
};